import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Badge,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
} from '@chakra-ui/react';
import fetchWithAuth from '../../utils/fetchWithAuth';

/**
 * QualityPanel - Response quality analysis and flagging
 */
function QualityPanel({ poll, pollId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [qualityThreshold, setQualityThreshold] = useState(50.0);
  const toast = useToast();

  useEffect(() => {
    fetchQualitySummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchQualitySummary = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/polls/${pollId}/quality-summary/`);

      if (response.ok) {
        const data = await response.json();
        setSummary(data.quality_summary || null);
      }
    } catch (err) {
      console.error('Error fetching quality summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunAnalysis = async () => {
    try {
      setRunningAnalysis(true);

      const response = await fetchWithAuth(`/api/polls/${pollId}/quality-analysis/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auto_flag: true,
          quality_threshold: qualityThreshold,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to run quality analysis');
      }

      const data = await response.json();
      const results = data.analysis_results;

      toast({
        title: 'Analysis Complete',
        description: `Flagged ${results.flagged_count} low-quality responses (${results.flagged_percentage}%)`,
        status: 'success',
        duration: 5000,
      });

      // Refresh summary
      await fetchQualitySummary();
    } catch (err) {
      console.error('Error running analysis:', err);
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setRunningAnalysis(false);
    }
  };

  if (loading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text>Loading quality analysis...</Text>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Instructions */}
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Box>
          <Text fontWeight="medium">Response Quality Analysis</Text>
          <Text fontSize="sm" mt={1}>
            Identifies low-quality responses including speeders (completed too fast) and
            straight-liners (same answer for all questions). Flagged responses can be
            excluded from analysis or downweighted.
          </Text>
        </Box>
      </Alert>

      {/* Configuration */}
      <Box
        p={5}
        bg="white"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Heading size="sm" mb={4}>Analysis Settings</Heading>
        <HStack spacing={4} align="end">
          <FormControl maxW="300px">
            <FormLabel fontSize="sm">Quality Threshold (0-100)</FormLabel>
            <NumberInput
              value={qualityThreshold}
              onChange={(_, val) => setQualityThreshold(val)}
              min={0}
              max={100}
              step={5}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="xs" color="gray.600" mt={1}>
              Responses below this score will be flagged
            </Text>
          </FormControl>

          <Button
            colorScheme="blue"
            onClick={handleRunAnalysis}
            isLoading={runningAnalysis}
            loadingText="Analyzing..."
          >
            Run Quality Analysis
          </Button>
        </HStack>
      </Box>

      {/* Summary Statistics */}
      {summary && summary.total_respondents > 0 ? (
        <>
          <Box>
            <Heading size="md" mb={4}>Quality Summary</Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <Stat
                p={4}
                bg="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <StatLabel>Total Responses</StatLabel>
                <StatNumber>{summary.total_respondents}</StatNumber>
                <StatHelpText>Completed surveys</StatHelpText>
              </Stat>

              <Stat
                p={4}
                bg="green.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="green.200"
              >
                <StatLabel>High Quality</StatLabel>
                <StatNumber color="green.700">
                  {summary.high_quality_count}
                </StatNumber>
                <StatHelpText>
                  {summary.high_quality_percentage}% of total
                </StatHelpText>
              </Stat>

              <Stat
                p={4}
                bg="orange.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="orange.200"
              >
                <StatLabel>Flagged</StatLabel>
                <StatNumber color="orange.700">
                  {summary.flagged_count}
                </StatNumber>
                <StatHelpText>
                  {summary.flagged_percentage}% of total
                </StatHelpText>
              </Stat>

              <Stat
                p={4}
                bg="blue.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="blue.200"
              >
                <StatLabel>Mean Quality Score</StatLabel>
                <StatNumber>{summary.mean_quality_score}</StatNumber>
                <StatHelpText>Out of 100</StatHelpText>
              </Stat>
            </SimpleGrid>
          </Box>

          {/* Quality Distribution */}
          <Box
            p={5}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
          >
            <Heading size="sm" mb={4}>Quality Distribution</Heading>
            <VStack spacing={4} align="stretch">
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    High Quality Responses
                  </Text>
                  <Badge colorScheme="green">
                    {summary.high_quality_percentage}%
                  </Badge>
                </HStack>
                <Progress
                  value={summary.high_quality_percentage}
                  colorScheme="green"
                  size="lg"
                  borderRadius="md"
                />
              </Box>

              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Flagged Responses
                  </Text>
                  <Badge colorScheme="orange">
                    {summary.flagged_percentage}%
                  </Badge>
                </HStack>
                <Progress
                  value={summary.flagged_percentage}
                  colorScheme="orange"
                  size="lg"
                  borderRadius="md"
                />
              </Box>
            </VStack>
          </Box>

          {/* Quality Issues Breakdown */}
          <Box
            p={5}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.200"
          >
            <Heading size="sm" mb={4}>Quality Issues Detected</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} bg="red.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Speeders
                </Text>
                <HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="red.700">
                    {summary.speeders_count}
                  </Text>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.600">
                      Completed too quickly
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {((summary.speeders_count / summary.total_respondents) * 100).toFixed(1)}% of total
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              <Box p={4} bg="yellow.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Straight-Liners
                </Text>
                <HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="yellow.700">
                    {summary.straight_liners_count}
                  </Text>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.600">
                      Same answer for all
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {((summary.straight_liners_count / summary.total_respondents) * 100).toFixed(1)}% of total
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Recommendations */}
          <Alert
            status={summary.flagged_percentage < 10 ? 'success' : summary.flagged_percentage < 25 ? 'warning' : 'error'}
            variant="left-accent"
          >
            <AlertIcon />
            <Box>
              <Text fontWeight="medium">Recommendation</Text>
              <Text fontSize="sm" mt={1}>
                {summary.flagged_percentage < 10 && (
                  <>
                    Your data quality is excellent ({summary.high_quality_percentage}% high quality).
                    Proceed with confidence in your analysis.
                  </>
                )}
                {summary.flagged_percentage >= 10 && summary.flagged_percentage < 25 && (
                  <>
                    {summary.flagged_percentage}% of responses are flagged. Consider excluding
                    flagged responses from analysis or applying quality-based weighting.
                  </>
                )}
                {summary.flagged_percentage >= 25 && (
                  <>
                    High percentage of low-quality responses ({summary.flagged_percentage}%).
                    Review your survey design and distribution method. Consider excluding
                    flagged responses from analysis.
                  </>
                )}
              </Text>
            </Box>
          </Alert>
        </>
      ) : (
        <Alert status="info">
          <AlertIcon />
          {summary?.message || 'No responses available for quality analysis. Run analysis once you have completed responses.'}
        </Alert>
      )}
    </VStack>
  );
}

export default QualityPanel;
